import { REDIRECT_URL } from '@constants/routes';
import { useGenericMutation } from '@hooks/use-generic-mutation';
import { useQueryClient } from '@tanstack/react-query';
import { request } from '@utils/request/request';
import { RequestMethod } from '@utils/request/request.type';

import { AuthTokenResponse, useGetTokenQueryKey } from './type';
import { useTokenManagement } from './use-token-management';

// Singleton to prevent multiple simultaneous refresh token calls
let ongoingRefreshPromise: Promise<AuthTokenResponse> | null = null;

export const requireRefreshedToken = async (refreshToken: string) => {
  // If there's already a refresh in progress, return that promise
  if (ongoingRefreshPromise) {
    return ongoingRefreshPromise;
  }

  const url = `${import.meta.env.VITE_AUTH_SERVICE_HOST}/v1/refresh-token`;

  // Create new refresh promise
  ongoingRefreshPromise = request({ url, method: RequestMethod.POST, body: { refreshToken } })
    .then((response: AuthTokenResponse) => {
      ongoingRefreshPromise = null; // Clear the promise on success
      return response;
    })
    .catch((error) => {
      ongoingRefreshPromise = null; // Clear the promise on error
      throw error;
    });

  return ongoingRefreshPromise;
};

export const useRequireAuthTokens = ({ refreshToken }: { refreshToken: string }) => {
  const queryClient = useQueryClient();
  const { clearTokens } = useTokenManagement();

  return useGenericMutation<AuthTokenResponse>(
    [useGetTokenQueryKey],
    async () => requireRefreshedToken(refreshToken),
    {
      retry: 1,
      onSuccess: (data) => {
        if (data) queryClient.setQueryData([useGetTokenQueryKey], data);
      },
      onError: () => {
        clearTokens();
        const currentUrl = globalThis.location.href;
        globalThis.location.href = `${REDIRECT_URL}=${currentUrl}`;
      },
    },
    true
  );
};
